import { SeasonalPeriod } from '../types/ferry';

export class SeasonalService {
  /**
   * Check if a given date falls within seasonal operation periods
   */
  static isDateInSeason(date: Date, seasonalOperation?: SeasonalPeriod): boolean {
    if (!seasonalOperation) {
      // No seasonal restrictions - operates year-round
      return true;
    }

    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const dateString = `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    return seasonalOperation.periods.some(period => {
      return dateString >= period.start && dateString <= period.end;
    });
  }

  /**
   * Get the next available seasonal period
   */
  static getNextSeasonalPeriod(seasonalOperation?: SeasonalPeriod): { start: string; end: string } | null {
    if (!seasonalOperation) {
      return null;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    
    for (const period of seasonalOperation.periods) {
      const [startMonth, startDay] = period.start.split('-').map(Number);
      const [endMonth, endDay] = period.end.split('-').map(Number);
      
      const startDate = new Date(currentYear, startMonth - 1, startDay);
      const endDate = new Date(currentYear, endMonth - 1, endDay);
      
      if (now <= endDate) {
        return {
          start: startDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
          }),
          end: endDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
          })
        };
      }
    }

    // If no period found this year, check next year's first period
    const firstPeriod = seasonalOperation.periods[0];
    if (firstPeriod) {
      const [startMonth, startDay] = firstPeriod.start.split('-').map(Number);
      const [endMonth, endDay] = firstPeriod.end.split('-').map(Number);
      
      const startDate = new Date(currentYear + 1, startMonth - 1, startDay);
      const endDate = new Date(currentYear + 1, endMonth - 1, endDay);
      
      return {
        start: startDate.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        }),
        end: endDate.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        })
      };
    }

    return null;
  }

  /**
   * Get all seasonal periods formatted for display
   */
  static getFormattedSeasonalPeriods(seasonalOperation?: SeasonalPeriod): string[] {
    if (!seasonalOperation) {
      return [];
    }

    const currentYear = new Date().getFullYear();
    
    return seasonalOperation.periods.map(period => {
      const [startMonth, startDay] = period.start.split('-').map(Number);
      const [endMonth, endDay] = period.end.split('-').map(Number);
      
      const startDate = new Date(currentYear, startMonth - 1, startDay);
      const endDate = new Date(currentYear, endMonth - 1, endDay);
      
      return `${startDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      })} - ${endDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      })}`;
    });
  }
}